<script lang="ts" setup>
import { Button, TextInputBlock } from '@clovyr/bed';
import type { Deployment } from '@clovyr/pollen';
import type { Manifest } from '@clovyr/pollen/manifest';

import Modal from '@/components/elements/Modal.vue';
import { usePollenStore } from '@/stores/pollen_store';

interface LibraryAppDetail {
  app: Manifest;
  deployment: Deployment;

  /**
   * When true, simply closes the modal instead of redirecting to Library
   */
  close?: boolean;
}
const props = defineProps<LibraryAppDetail>();
const emit = defineEmits(['close']);

const pollenStore = usePollenStore();
const router = useRouter();

const isShared =
  props.deployment.accessPolicy !== undefined && props.deployment.accessPolicy !== 'owner';
const heading = isShared
  ? `Leave ${props.deployment.instanceName}?`
  : `Are you sure you want to delete ${props.deployment.instanceName}?`;

const confirmDelete = ref('');
const errorMessage = ref('');

async function handleDelete() {
  const confirmation = isShared ? 'LEAVE' : 'DELETE';
  if (!confirmDelete.value || confirmDelete.value.trim().toUpperCase() !== confirmation) {
    // TODO: show invalid entry, etc. maybe disable button until it's correct?
    errorMessage.value = `Must type '${confirmation.toLowerCase()}' in the above box to continue.`;
    return;
  }
  if (props.close) {
    await pollenStore.deleteDeployment(props.deployment);
    emit('close');
    return;
  }
  void router.push({ name: 'Library' }).then(() => {
    void nextTick(async () => {
      await pollenStore.deleteDeployment(props.deployment);
    });
  });
}
</script>

<template>
  <Modal :headingText="heading" @close="$emit('close')">
    <template v-slot:body>
      <div class="delete-app">
        <TextInputBlock
          :label="
            isShared
              ? 'Type “LEAVE” to remove this app from your library.'
              : 'Type “DELETE” to confirm. The app and all its data will be erased.'
          "
          class="delete-app__input"
          :error-message="errorMessage"
          @input="(e) => (confirmDelete = e.target.value)"
          @keyup.enter="handleDelete"
          focus
        />
      </div>
    </template>

    <template v-slot:controls-right>
      <Button :label="isShared ? 'leave' : 'delete'" :reversed="true" @click="handleDelete" />
    </template>
  </Modal>
</template>
<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
:deep(.error-message) {
  color: red;
}
</style>
